/**
 * @typedef {'Accordion'|'Dropdown'} FacetAnimationType
 */

class FilterFacet extends window.BAO.CustomElement({
  SuperClass: HTMLDetailsElement,
}) {
  static get requiredElements () {
    return ['summary', 'content']
  }

  isOpen = false

  constructor () {
    super()

    /**
     * @type {Animation|null}
     * @public
     */
    this.animation = null

    /**
     * @type {Boolean}
     * @public
     */
    this.isClosing = false

    /**
     * @type {Boolean}
     * @public
     */
    this.isExpanding = false
  }

  setupListeners () {
    super.setupListeners()

    this.onSummaryClick = this.onSummaryClick.bind(this)

    this.listeners.add(this, 'click', this.onSummaryClick)
  }

  /**
   * @param {MouseEvent} e
   *
   * @returns {void}
   */
  onSummaryClick (e) {
    if (!e.target.closest(this.els.summary.selector)) return

    e.preventDefault()

    if (this.getAnimation() === 'Accordion') {
      this.style.overflow = 'hidden'
    }

    if (this.isClosing || !this.open) {
      this.start()
    } else if (this.isExpanding || this.open) {
      this.close()
    }
  }

  start () {
    this.style.height = `${this.offsetHeight}px`

    this.open = true
    this.dispatchEvent(new CustomEvent('filter-facet:open', { bubbles: true }))

    window.requestAnimationFrame(() => this.expand())
  }

  close () {
    this.dispatchEvent(
      new CustomEvent('filter-facet:close', { bubbles: true }),
    )

    this.shrink()
  }

  shrink () {
    this.isClosing = true

    const startHeight = this.offsetHeight
    const endHeight = this.els.summary.element.offsetHeight

    if (this.animation) {
      this.animation.cancel()
    }

    switch (this.getAnimation()) {
      case 'Accordion':
        this.animation = this.accordionAnimation(startHeight, endHeight)

        break
      case 'Dropdown':
        this.animation = this.dropdownAnimation(
          1,
          0,
          'none',
          'translateY(-15px)',
        )

        break
    }

    this.animation.onfinish = () => this.onAnimationFinish(false)
    this.animation.oncancel = () => (this.isClosing = false)
  }

  expand () {
    this.isExpanding = true

    const startHeight = this.offsetHeight
    const endHeight =
      this.els.summary.element.offsetHeight + this.content.offsetHeight

    if (this.animation) {
      this.animation.cancel()
    }

    switch (this.getAnimation()) {
      case 'Accordion':
        this.animation = this.accordionAnimation(startHeight, endHeight)

        break
      case 'Dropdown':
        this.animation = this.dropdownAnimation(
          0,
          1,
          'translateY(-15px)',
          'none',
        )

        break
    }

    this.animation.onfinish = () => this.onAnimationFinish(true)
    this.animation.oncancel = () => (this.isExpanding = false)
  }

  getAnimation () {
    if (window.BAO.MediaBreakpoints.isCustomLg) {
      return this.desktopAnimationStyle
    }

    return this.mobileAnimationStyle
  }

  /**
   * @param {number} startHeight
   * @param {number} endHeight
   */
  accordionAnimation (startHeight, endHeight) {
    const _startHeight = `${startHeight}px`
    const _endHeight = `${endHeight}px`

    return this.animate(
      {
        height: [_startHeight, _endHeight],
      },
      {
        duration: 400,
        easing: this.easing,
      },
    )
  }

  /**
   * @param {number} opacityFrom
   * @param {number} opacityTo
   * @param {string} translateFrom
   * @param {string} translateTo
   */
  dropdownAnimation (opacityFrom, opacityTo, translateFrom, translateTo) {
    return this.content.animate(
      {
        opacity: [opacityFrom, opacityTo],
        transform: [translateFrom, translateTo],
      },
      { duration: 400, easing: 'ease' },
    )
  }

  /**
   * @param {Boolean} open
   *
   * @returns {void}
   */
  onAnimationFinish (open) {
    this.open = open
    this.animation = null

    this.isClosing = false
    this.isExpanding = false

    this.style.height = ''
    this.style.overflow = ''
  }

  /**
   * @returns {HTMLElement}
   */
  get content () {
    return window.BAO.Utils.getElement(this.els.content.selector, {
      context: this,
    })
  }

  get easing () {
    return 'cubic-bezier(0.87, 0, 0.13, 1)'
  }

  /**
   * @returns {FacetAnimationType}
   */
  get mobileAnimationStyle () {
    return this.getAttribute('data-mobile-animation-style') ?? 'Accordion'
  }

  /**
   * @returns {FacetAnimationType}
   */
  get desktopAnimationStyle () {
    return this.getAttribute('data-desktop-animation-style') ?? 'Dropdown'
  }
}

if (!customElements.get('filter-facet')) {
  customElements.define('filter-facet', FilterFacet, { extends: 'details' })
}
